import { ActionRequestStatus, ActionRequestType } from '@billing/validation';
import { Button, Menu, Loader, Flex } from '@mantine/core';
import { IconFileText, IconChevronDown } from '@tabler/icons-react';
import {
  ActionRequest,
  ActionResponse,
  Provider,
  ResponseAttachment,
} from 'interfaces';
import { ResolveResponseButton } from './ResolveButton';
import { ActionCard } from './ActionCard';

export const ResponseCard = ({
  request,
  response,
  attachments,
  attachmentLoading,
  onSatisfyResponse,
  onCancelResponse,
  openRejectDialog,
  canResolve = true,
  statusColor,
  typeColor,
  typeLabel,
  testID,
}: {
  request: ActionRequest;
  response: ActionResponse;
  attachments: ResponseAttachment[];
  attachmentLoading: boolean;
  onSatisfyResponse: (
    request: ActionRequest,
    response: ActionResponse
  ) => Promise<void>;
  onCancelResponse: (
    request: ActionRequest,
    response: ActionResponse
  ) => Promise<void>;
  openRejectDialog: (request: ActionRequest, response: ActionResponse) => void;
  canResolve?: boolean;
  statusColor: Record<ActionRequestStatus, string>;
  typeColor: Record<ActionRequestType, string>;
  typeLabel: string;
  testID?: string;
}) => {
  const showResolve =
    canResolve &&
    !!response &&
    request.status === ActionRequestStatus.SUBMITTED;

  return (
    <ActionCard
      title="Provider"
      reviewer={response.provider as Partial<Provider>}
      date={response.createdAt.toString()}
      message={response.message}
      status={request.status}
      type={request.type}
      typeLabel={typeLabel}
      statusColor={statusColor}
      typeColor={typeColor}
      testID={testID}
      headerRight={
        showResolve ? (
          <ResolveResponseButton
            request={request}
            response={response}
            onSatisfy={onSatisfyResponse}
            onCancel={onCancelResponse}
            openRejectDialog={openRejectDialog}
          />
        ) : null
      }
      bodyRight={
        attachments.length ? (
          <ViewAttachments
            attachments={attachments}
            attachmentLoading={attachmentLoading}
          />
        ) : null
      }
    />
  );
};

const ViewAttachments = ({
  attachments,
  attachmentLoading,
}: {
  attachments: ResponseAttachment[];
  attachmentLoading: boolean;
}) => {
  if (!attachments.length) return null;

  if (attachmentLoading) {
    return (
      <Flex align="center" justify={'center'} gap={4} w={'10%'}>
        <Loader size="xs" type="dots" />
      </Flex>
    );
  }

  if (attachments.length === 1) {
    const attachment = attachments[0];
    return (
      <Button
        size="xs"
        variant="subtle"
        leftSection={<IconFileText size={14} />}
        data-testid="response-attachments-view"
        onClick={() =>
          window.open(attachment.url, '_blank', 'noopener,noreferrer')
        }
      >
        View
      </Button>
    );
  }

  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<IconFileText size={14} />}
          rightSection={<IconChevronDown size={14} />}
          data-testid="response-attachments-view"
        >
          View ({attachments.length})
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        {attachments.map((attachment) => {
          return (
            <Menu.Item
              key={attachment.key}
              data-testid={`response-attachment-${attachment.key}`}
              onClick={() =>
                window.open(attachment.url, '_blank', 'noopener,noreferrer')
              }
            >
              {attachment.filename}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
